import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Moon, Sun, Menu, X, LogOut, User } from 'lucide-react';

export const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoIntroKey, setLogoIntroKey] = useState(0);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/tools', label: 'Tools' },
    { path: '/changelog', label: 'Changelog' },
    { path: '/settings', label: 'Settings' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  useEffect(() => {
    if (location.pathname === '/') {
      setLogoIntroKey((prev) => prev + 1);
    }
  }, [location.pathname]);

  return (
    <nav className="sticky top-0 z-50 pt-3" data-testid="navbar">
      <div className="nav-shell mx-auto w-[min(96%,84rem)] min-[1600px]:w-[min(97%,112rem)] min-[1900px]:w-[min(97%,124rem)] rounded-[1.3rem] min-[1600px]:rounded-[1.55rem]">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center h-16 min-[1600px]:h-[4.6rem] min-[1900px]:h-[5rem]">
            {/* Logo */}
            <Link to="/" className="z-10 flex items-center gap-3 group" data-testid="navbar-logo">
              <img
                key={logoIntroKey}
                src="/images/pkec-logo-mark.svg"
                alt="PKEC Tools"
                className={`logo-mark h-11 min-[1600px]:h-12 min-[1900px]:h-14 w-auto shrink-0 object-contain ${location.pathname === '/' ? 'logo-intro' : ''}`}
              />
              <span className="font-serif text-xl min-[1600px]:text-2xl font-semibold tracking-tight text-foreground/95 group-hover:text-foreground transition-colors">
                PKEC<span className="text-primary">Tools</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="absolute left-1/2 -translate-x-1/2 hidden md:flex items-center gap-1.5 min-[1600px]:gap-2 rounded-full glass-surface glass-surface--default p-1 min-[1600px]:p-1.5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                  className={`px-4 py-2 min-[1600px]:px-5 min-[1600px]:py-2.5 rounded-full text-sm min-[1600px]:text-base font-medium transition-all duration-300 border ${
                    isActive(link.path)
                      ? 'border-primary/35 bg-primary/20 text-foreground shadow-[0_8px_18px_-12px_rgba(0,0,0,0.95),inset_0_1px_0_rgba(255,255,255,0.28)]'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/65'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Actions */}
            <div className="z-10 ml-auto flex items-center gap-2">
              {/* User info */}
              {isAuthenticated && user && (
                <div className="hidden sm:flex items-center gap-2 min-[1600px]:gap-2.5 px-3 py-1.5 min-[1600px]:px-4 min-[1600px]:py-2 rounded-full glass-surface glass-surface--default">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm min-[1600px]:text-base text-muted-foreground max-w-[150px] min-[1600px]:max-w-[220px] truncate">
                    {user.email}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="h-7 px-2 text-muted-foreground hover:text-foreground"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
                className="glass-surface glass-surface--subtle rounded-full"
              >
                {theme === 'light' ? (
                  <Moon className="h-5 w-5" />
                ) : (
                  <Sun className="h-5 w-5" />
                )}
              </Button>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="glass-surface glass-surface--subtle md:hidden rounded-full"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border/60">
              {isAuthenticated && user && (
                <div className="px-4 py-3 mb-2 border-b border-border/60">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="w-4 h-4" />
                    <span className="truncate">{user.email}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="mt-2 w-full justify-start text-muted-foreground"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </Button>
                </div>
              )}
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-primary/20 text-foreground border border-primary/35'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/60 border border-transparent'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
